<template>
  <div class="partners-page">
    <div class="glow-sphere"></div>
    <div class="grid-overlay"></div>

    <div class="content-container">
      <section class="hero-section v-reveal">
        <div class="meta-tag">PARTNERSHIP & ECOSYSTEM</div>
        <h1 class="hero-title">
          与优秀同行<br />
          <span class="text-gradient">共筑社区生态</span>
        </h1>
        <p class="hero-sub">
          来秋社区相信，真正长久的价值，来自开放、真诚、长期的合作。
          我们期待与每一位志同道合的伙伴，一起成长，一起创造。
        </p>
      </section>

      <section class="partner-matrix v-reveal">
        <div class="group-label">01 / 合作品牌</div>
        <div class="logo-grid">
          <div v-for="n in 12" :key="n" class="logo-card">
            <div class="logo-placeholder">BRAND_{{ n < 10 ? '0' + n : n }}</div>
                <div class="hover-border"></div>
            </div>
          </div>
      </section>

      <section class="philosophy-section">
        <div class="flex-layout">
          <div class="left-box v-reveal">
            <div class="group-label">02 / 合作理念</div>
            <h2 class="title">开放 · 真诚 · 共赢</h2>
          </div>
          <div class="right-box v-reveal">
            <p class="philosophy-text">
              我们不搞封闭，不设壁垒，坚持以开放心态连接每一份力量。
              来秋社区希望与伙伴一起，用技术服务用户，用温暖连接彼此，
              让生态更健康，让价值更长久。
            </p>
          </div>
        </div>
      </section>

      <section class="support-system">
        <div class="group-label v-reveal">03 / 合作支持</div>
        <div class="support-grid">
          <div v-for="(item, index) in supportItems" :key="index" class="support-card v-reveal">
            <div class="card-num">{{ item.index }}</div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </div>
        </div>
      </section>

      <section class="join-section v-reveal">
        <div class="join-card">
          <div class="join-content">
            <h2>成为来秋合作伙伴</h2>
            <p>无论你是品牌方、创作者、技术团队还是校园组织，我们都欢迎真诚、长期的合作。</p>
            <div class="btn-group">
              <button class="primary-btn">申请合作</button>
              <button class="secondary-btn">了解更多</button>
            </div>
          </div>
          <div class="line-deco"></div>
        </div>
      </section>

      <footer class="footer-minimal v-reveal">
        <div class="sep-line"></div>
        <p class="footer-slogan">GROW TOGETHER, PARTNER FOREVER.</p>
        <p class="copyright">© 2026 LAIQIU COMMUNITY. ALL RIGHTS RESERVED.</p>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { onMounted } from 'vue'

const supportItems = [
  { index: '01', title: '内容共创', desc: '联合产出优质内容，覆盖技术、生活、创作、兴趣等多元方向。' },
  { index: '02', title: '品牌曝光', desc: '依托来秋社区流量，精准触达年轻、优质、高活跃用户。' },
  { index: '03', title: '活动联动', desc: '共同举办线上线下活动，包括分享会、赛事、体验日、校园合作。' },
  { index: '04', title: '技术支持', desc: '共享平台能力与资源，为合作项目提供稳定、安全的技术保障。' }
]

onMounted(() => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('active');
      }
    });
  }, { threshold: 0.1 });

  document.querySelectorAll('.v-reveal').forEach(el => observer.observe(el));
});
</script>

<style scoped lang="scss">
/* 变量定义 */
$bg-black: #050505;
$accent-gold: #c9a063;
$text-dim: #888;
$border-alpha: rgba(255, 255, 255, 0.08);

.partners-page {
  background-color: $bg-black;
  color: #fff;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  font-family: 'Inter', -apple-system, sans-serif;
  padding-bottom: 100px;
}

/* 氛围元素 */
.glow-sphere {
  position: fixed;
  top: -10%;
  right: -10%;
  width: 50vw;
  height: 50vw;
  background: radial-gradient(circle, rgba($accent-gold, 0.05) 0%, transparent 70%);
  pointer-events: none;
  z-index: 0;
}

.grid-overlay {
  position: fixed;
  inset: 0;
  background-image: radial-gradient($border-alpha 1px, transparent 1px);
  background-size: 40px 40px;
  mask-image: linear-gradient(to bottom, black, transparent);
  pointer-events: none;
  z-index: 1;
}

.content-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px;
  position: relative;
  z-index: 10;
}

/* 动效 */
.v-reveal {
  opacity: 0;
  transform: translateY(30px);
  transition: all 1s cubic-bezier(0.2, 1, 0.3, 1);

  &.active {
    opacity: 1;
    transform: translateY(0);
  }
}

.group-label {
  font-family: monospace;
  font-size: 12px;
  color: $accent-gold;
  letter-spacing: 3px;
  margin-bottom: 30px;
  text-transform: uppercase;
}

/* Hero Section */
.hero-section {
  height: 80vh;
  display: flex;
  flex-direction: column;
  justify-content: center;

  .meta-tag {
    font-size: 11px;
    letter-spacing: 5px;
    color: $text-dim;
    margin-bottom: 20px;
  }

  .hero-title {
    font-size: clamp(3rem, 8vw, 5.5rem);
    font-weight: 700;
    line-height: 1.1;
    margin-bottom: 30px;
    letter-spacing: -2px;
  }

  .text-gradient {
    background: linear-gradient(to right, #fff, $accent-gold);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .hero-sub {
    font-size: 18px;
    color: $text-dim;
    max-width: 600px;
    line-height: 1.8;
  }
}

/* Partner Matrix */
.partner-matrix {
  margin-bottom: 180px;

  .logo-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1px;
    background: $border-alpha;
    border: 1px solid $border-alpha;
  }

  .logo-card {
    aspect-ratio: 16/9;
    background: $bg-black;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    transition: background 0.4s;

    &:hover {
      background: #0a0a0a;

      .logo-placeholder {
        color: #fff;
      }
    }

    .logo-placeholder {
      font-size: 14px;
      color: #333;
      letter-spacing: 2px;
      font-weight: 600;
      transition: color 0.4s;
    }
  }
}

/* Philosophy */
.philosophy-section {
  margin-bottom: 180px;

  .flex-layout {
    display: flex;
    justify-content: space-between;
    gap: 100px;
  }

  .left-box {
    flex: 1;

    .title {
      font-size: 36px;
      font-weight: 600;
    }
  }

  .right-box {
    flex: 1.5;

    .philosophy-text {
      font-size: 20px;
      line-height: 1.8;
      color: $text-dim;
      font-weight: 300;
    }
  }
}

/* Support System */
.support-system {
  margin-bottom: 180px;

  .support-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 40px;
  }

  .support-card {
    padding: 60px;
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid $border-alpha;
    position: relative;

    .card-num {
      font-size: 12px;
      color: $accent-gold;
      margin-bottom: 20px;
      font-family: monospace;
    }

    h3 {
      font-size: 24px;
      margin-bottom: 15px;
    }

    p {
      color: $text-dim;
      line-height: 1.7;
      font-size: 15px;
    }
  }
}

/* Join Us */
.join-section {
  margin-bottom: 150px;

  .join-card {
    background: #111;
    padding: 100px 60px;
    text-align: center;
    position: relative;
    overflow: hidden;

    h2 {
      font-size: 42px;
      margin-bottom: 20px;
    }

    p {
      color: $text-dim;
      margin-bottom: 50px;
      max-width: 600px;
      margin-inline: auto;
    }

    .btn-group {
      display: flex;
      justify-content: center;
      gap: 20px;
    }

    .primary-btn {
      background: $accent-gold;
      color: #000;
      border: none;
      padding: 16px 40px;
      font-weight: 600;
      cursor: pointer;
      transition: 0.3s;

      &:hover {
        transform: translateY(-3px);
        box-shadow: 0 10px 20px rgba($accent-gold, 0.2);
      }
    }

    .secondary-btn {
      background: transparent;
      color: #fff;
      border: 1px solid rgba(255, 255, 255, 0.1);
      padding: 16px 40px;
      cursor: pointer;
      transition: 0.3s;

      &:hover {
        background: rgba(255, 255, 255, 0.05);
      }
    }
  }
}

/* Footer */
.footer-minimal {
  text-align: center;
  padding-top: 50px;

  .sep-line {
    width: 1px;
    height: 80px;
    background: linear-gradient($accent-gold, transparent);
    margin: 0 auto 40px;
  }

  .footer-slogan {
    font-size: 12px;
    letter-spacing: 6px;
    color: #444;
    margin-bottom: 20px;
  }

  .copyright {
    font-size: 10px;
    color: #222;
  }
}

/* 响应式适配 */
@media (max-width: 992px) {
  .partner-matrix .logo-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .philosophy-section .flex-layout {
    flex-direction: column;
    gap: 40px;
  }

  .support-system .support-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .hero-section .hero-title {
    font-size: 2.5rem;
  }

  .join-section .join-card {
    padding: 60px 30px;

    h2 {
      font-size: 28px;
    }
  }

  .btn-group {
    flex-direction: column;
  }
}
</style>